import React from 'react';
import { style } from 'typestyle';
import Skeleton from '@material-ui/lab/Skeleton';
import { StyledAccordion } from 'src/components/StyledAccordion/StyledAccordion';
import { GemIconLabelled } from 'src/components/Header/Icons';
import { MacrosProps } from 'src/common-ui/components/Macros/Macros.types';
import { getDirectionClasses } from 'src/common-ui/components/Macros/Macros.utils';
import { positiveTrend, negativeTrend, neutralTrend, fadedNeutral, primaryTextColor } from 'src/common-ui/theme';

export interface MacroDescriptionProps {
  dataLoaded: boolean;
  /** Positional array projected from the view config:
   *  [0] score item  – primary.rendered = score value, primary.label = "Score"
   *  [1] worksItem   – primary.label = section header ("Why it Works"), primary.rendered = value
   *  [2] descItem    – subMetrics[0..n].rendered = bullet descriptions
   *  [3] (unused in render; "Powered by Gemini" is hardcoded per config guidance)
   */
  macros: MacrosProps[];
  overrideTransition?: boolean;
}

const containerStyle = style({
  display: 'flex',
  alignItems: 'center',
  gap: '1.5rem',
  width: '100%',
  $nest: {
    // Score donut ring – mirrors the .middle-container pattern from ChoiceProductivity
    '.middle-container': {
      flexShrink: 0,
      width: '132px',
      height: '132px',
      borderRadius: '50%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      $nest: {
        '&.positive': { border: `4px solid ${positiveTrend}` },
        '&.negative': { border: `4px solid ${negativeTrend}` },
        '&.zero': { border: `4px solid ${neutralTrend}` },
      },
    },
    '.score-value': {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.1,
      color: primaryTextColor,
    },
    '.score-label': {
      fontSize: '0.65rem',
      color: fadedNeutral,
      marginTop: '2px',
    },
  },
});

export const infoPanelStyle = style({
  $debugName: 'infoPanelStyle',
  flex: 1,
  backgroundColor: '#f0f0f0',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  $nest: {
    '.info-row': {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      $nest: {
        '& > *': {
          margin: '1.75rem 1rem',
        },
      },
    },
    '.why-section': {
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'center',
    },
    '.why-header': {
      fontSize: '0.65rem',
      // should be fadedNeutral, but it has been declared that we match the mock exactly, so this is the exact color from the mock
      color: '#676767',
    },
    '.why-value': {
      fontSize: '0.9rem',
      fontWeight: 600,
      // we don't normally use full black because in the past it made people sad but it doesn't anymore because that's whats in the mock
      color: '#000',
    },
    '.section-divider': {
      width: '1px',
      alignSelf: 'stretch',
      backgroundColor: '#ccc',
      margin: '1rem 1rem 1rem 0.5rem', // this is kind of an override on the margin applied to the immediate children of .info-row, so we can make the divided a little taller than it's siblings
    },
    '.descriptions-list': {
      flex: 1,
      margin: 0,
      paddingLeft: '1.2rem',
      color: '#000',
    },
    '.descriptions-list li': {
      fontSize: '0.9rem',
      fontWeight: 600,
      lineHeight: 1.5,
      paddingLeft: '0.6rem',
    },
  },
});

export const MacroDescription = ({ dataLoaded, macros, overrideTransition = false }: MacroDescriptionProps) => {
  const [scoreItem, worksItem, descItem] = macros;

  const { middleContainerClass } = getDirectionClasses(scoreItem?.metrics.primary.direction);

  return (
    <StyledAccordion expanded={true} title="Look Snapshot" overrideTransition={overrideTransition}>
      <div className={containerStyle}>
        {!dataLoaded ? (
          <Skeleton variant="circle" animation="wave" width={100} height={100} />
        ) : (
          <div className={middleContainerClass}>
            <span className="score-value">{scoreItem?.metrics.primary.rendered}</span>
            <span className="score-label">{scoreItem?.metrics.primary.label}</span>
          </div>
        )}

        <div className={infoPanelStyle}>
          <div className="info-row">
            {!dataLoaded ? (
              <>
                <Skeleton variant="text" animation="wave" width={110} height={40} />
                <Skeleton variant="text" animation="wave" style={{ flex: 1, marginLeft: '1rem' }} height={40} />
              </>
            ) : (
              <>
                <div className="why-section">
                  <span className="why-header">{worksItem?.metrics.primary.label}</span>
                  <span className="why-value">{worksItem?.metrics.primary.rendered}</span>
                  <div className="powered-by-row">
                    <GemIconLabelled />
                  </div>
                </div>
                <div className="section-divider" />
                <ul className="descriptions-list">
                  {descItem?.metrics.subMetrics.map((metric, i) => <li key={i}>{metric.rendered}</li>)}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </StyledAccordion>
  );
};
